import classNames from 'classnames';
import { PropsWithChildren, ReactNode, useContext } from 'react';

import { RowStackContext } from './row-stack-context';
import { ECONOMY_ROW_LABEL_CLASS } from '../economy-theme';

export interface SettingsRowProps {
  label: ReactNode;
  className?: string;
  /**
   * `top` for rows whose value spans several lines, so the label sits beside the first
   * one instead of floating in the middle of the block.
   *
   * A prop rather than a caller-supplied `sm:items-start`: both classes would then land
   * on the same element, and Tailwind emits `items-start` before `items-center`, so the
   * default would silently win.
   */
  align?: 'center' | 'top';
}

/**
 * One label/value pair. Inside an `EconomyCard rowStack` the row is `display: contents`
 * from `sm` up, so its label and value become cells of the card's own three-column grid
 * rather than of a grid of their own — the label takes one column, the value the other
 * two, and the labels line up down the card. Below `sm` the wrapper is a plain grid and
 * the pair stacks.
 *
 * Outside that card the row keeps its own box and lays the same two columns out itself.
 * The labels then only line up with rows sharing that parent, but the pair still reads as
 * a label/value row instead of collapsing into whatever grid happens to be above it.
 *
 * Alignment sits on the cells, since a `contents` wrapper has no box to align within.
 */
export function SettingsRow({ label, className, align = 'center', children }: PropsWithChildren<SettingsRowProps>) {
  const cellAlign = align === 'center' ? 'sm:self-center' : 'sm:self-start';
  const inRowStack = useContext(RowStackContext);

  return (
    <div className={classNames('grid gap-1', inRowStack ? 'sm:contents' : 'sm:grid-cols-3 sm:gap-x-4', className)}>
      <div className={classNames(ECONOMY_ROW_LABEL_CLASS, cellAlign)}>{label}</div>
      <div className={classNames('min-w-0 sm:col-span-2', cellAlign)}>{children}</div>
    </div>
  );
}

export default SettingsRow;
